/**
 *
 * Communications
 *
 */

import React, { memo, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';

// styles
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import {
  flushStateCommunications,
  // list
  loadCommunications,
} from './actions';

import {
  makeSelectFirstRender,
  // list
  makeSelectItemsList,
  selectCommunicationsLoading,
  selectCommunicationsError,
  makeSelectItemsListFilteringParams,
  makeSelectItemsListNoMore,
  makeSelectItemsListTotalAmount,
  makeSelectItemsListCurrentPage,
} from './selectors';

// layout components
import ErrorBoundary from 'engine/ErrorBoundary';
import Preloader from 'components/Preloader';

// antd component
import { Layout, List, Empty } from 'antd';
const { Content } = Layout;

// assets
// import { LoadingOutlined } from '@ant-design/icons';
//import CustomIcons from 'components/CustomIcons';

//components
import SideListItem from 'components/SideListItem';
// inner communication components
import {
  CommunicationsHeadlineBar
} from './components';

// hooks
import { useRefState, useAuthRole } from 'utils/hooks';

// global user
import { useGetLocalActiveChild, useGetLocalUser } from 'engine/Contexts/User.context';

export function CommunicationsMobile({
  // main data
  itemsList,
  // states
  firstRender,
  history,
  // list
  error,
  loading,
  params,
  noMore,
  totalAmount,
  currentPage,
  // actions
  loadList,
  flushState,
  //default
  className,
}) {
  useInjectReducer({ key: 'communicationsMobile', reducer });
  useInjectSaga({ key: 'communicationsMobile', saga });

  let contextChild = useGetLocalActiveChild()(); // get from local 
  const localUser = useGetLocalUser()();
  const isParent = localUser?.role == 'parent';

  const [searchVal, setSearchVal] = useState('');
  const [filter, setFilter] = useState(
    params?.filterValue?.length > 0
      ? typeof params?.filterValue ==='string' ? params?.filterValue?.split(',') : params?.filterValue
      : [],
  );
  const [currentParams, currentParamsRef, setCurrentParams] = useRefState(
    params || null,
  );
  const [currentChild, setCurrentChild] = useState(!!isParent && contextChild?._id || null);

  const oldParams = history.location?.state?.oldParams;
  const cameFrom = history.location?.state?.cameFrom;
  const needRefresh = history.location?.state?.needRefresh;

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    setCurrentParams(params);
    if (
      !!needRefresh || 
      (
        (!itemsList || itemsList?.length <= 0) && !oldParams && cameFrom != ('single_communication')
      )
    ) {
      setCurrentParams(params);

      if (!!isParent) {
        loadList(params, currentChild, true);
      } else {
        loadList(params, null, true);
      }
    } else if ( (!itemsList || itemsList?.length <= 0) &&  oldParams &&  cameFrom ) {
      const newParamObj = {
        ...params,
        searchStr: oldParams?.searchStr || params?.searchStr,
        page: oldParams?.page || params?.page,
        filterValue: oldParams?.filterValue || params?.filterValue,
      };
      setSearchVal(newParamObj?.searchStr);
      setFilter(newParamObj?.filterValue);
      setCurrentParams(newParamObj);

      if (!!isParent) {
        loadList(newParamObj, currentChild, true);
      } else {
        loadList(newParamObj, null, true);
      }
      
    }

    return () => {
      // Anything in here is fired on component unmount.
      // Clearing the state after unmounting a component
      flushState();
    };
  }, []);

  // Ceck history location
  useEffect(() => {
    if (cameFrom && cameFrom == 'single_communication' && !firstRender) {
      if (oldParams) {
        const newParamObj = {
          ...currentParams,
          searchStr: oldParams?.searchStr || currentParams?.searchStr,
          page: oldParams?.page || currentParams?.page,
          filterValue: oldParams?.filterValue || currentParams?.filterValue,
        };
        setSearchVal(newParamObj?.searchStr);
        setFilter(newParamObj?.filterValue);
        setCurrentParams(newParamObj);

        if (!!isParent) {
          loadList(newParamObj, currentChild, false);
        } else {
          loadList(newParamObj, null, false);
        }
        
      }
    }
  }, [cameFrom]);

  // load communications if context active user will change
  useEffect(() => {
    if (!!isParent && contextChild?._id && currentChild != contextChild?._id) {
      setCurrentChild(contextChild?._id);
      loadList(currentParams, contextChild?._id, true);
    }
  }, [contextChild?._id]);

  const changeCurrentItem = useCallback(
    current => {
      history.push({
        pathname: `/communications/${current}`,
        search: '',
        hash: '',
        state: {
          oldParams: currentParams,
          goBack: '/communications',
        },
      });
    },
    [history, currentParams],
  );

  // Change filter params and load new item for infinite scroll
  const changeFilterParams = useCallback(
    newParams => {
      if (
        newParams &&
        Object.keys(newParams).length > 0 &&
        currentParamsRef?.current &&
        Object.keys(currentParamsRef?.current).length > 0 &&
        JSON.stringify(currentParamsRef?.current) != JSON.stringify(newParams)
      ) {
        const newParamObj = {
          ...currentParamsRef?.current,
          ...newParams,
        };
        setCurrentParams(newParamObj);
        loadList(newParamObj, currentChild, false);
      } else if (newParams && Object.keys(newParams).length > 0) {
        const newParamObj = { ...params, ...newParams };
        setCurrentParams(newParamObj);
        loadList(newParamObj, currentChild, false);
      }
    },
    [params, currentParams, currentParamsRef, currentChild],
  );

  /**
   * Search Actions
   */
  const onSearch = useCallback(
    searchValue => {
      setSearchVal(searchValue);
      setCurrentParams(prev => {
        const newVal = {
          ...prev,
          page: 1,
          searchStr: searchValue,
        };
        changeFilterParams(newVal);
        return newVal;
      });
    },
    [changeFilterParams],
  );

  /**
   * Filter Actions
   */
  const onFilter = useCallback(checkedValues => {
    setFilter(checkedValues || []);
    setCurrentParams(prev => {
      const newVal = {
        ...prev,
        page: 1,
        filterValue: checkedValues,
      };
      changeFilterParams(newVal);
      return newVal;
    });
  }, []);

  const resetFilter = useCallback((filter) => {
    setSearchVal('');
    setFilter([]);
    setCurrentParams(prev => {
      const newVal = {
        ...prev,
        page: 1,
        searchStr: '',
        filterValue: [],
      };
      changeFilterParams(newVal);
      return newVal;
    });
  }, []);

  /**
   * List Pagination
   */
  const listPaginationCallback = useCallback(
    page => changeFilterParams({ page: page }),
    [changeFilterParams],
  );
  let listPagination = useMemo(() => {
    if (Number(totalAmount) <= Number(params.limit)) {
      return false;
    } else {
      return {
        onChange: page => listPaginationCallback(page),
        showQuickJumper: false,
        defaultCurrent: 1,
        showLessItems: true,
        current: currentPage,
        pageSize: params?.limit || 4,
        total: totalAmount,
      };
    }
  }, [totalAmount, currentPage, params?.limit, listPaginationCallback]);

  let communicationHeaderOptions = {};
  if (!!isParent) {
    communicationHeaderOptions = {
      onAdd: () => {
        history.push({
          pathname: `/communications/nouvel`,
          state: {
            from: 'home',
            goBack: '/home',
            goBackTitle: 'Home',
          },
        });
      }
    }
  }

  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>Communications</title>
        <meta name="description" content="Description of Communications" />
      </Helmet>

      <Content className="main-content-container communication-page">

        <CommunicationsHeadlineBar
          title="Communications"
          loading={loading}
          params={currentParams}
          search={searchVal}
          filter={filter}
          onFilter={onFilter}
          onReset={resetFilter}
          onSearch={onSearch}
          {...communicationHeaderOptions}
        />

        <List
          header=""
          className="communication-mobile-list"
          itemLayout="vertical"
          size="large"
          pagination={listPagination}
          dataSource={itemsList}
          renderItem={(item, index) => {
            return (
              <SideListItem
                key={item._ID}
                id={item._ID}
                type={!!isParent ? "communications-parent" : "communications-teacher"}
                onClickAction={changeCurrentItem}
                searchString={searchVal}
                element={item}
              />
            );
          }}
          loading={{
            spinning: loading,
            indicator: <Preloader />,
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  'Aucune donnée n’a été trouvée concernant votre demande'
                }
              />
            ),
          }}
        />
      </Content>
    </Layout>
  );
}

CommunicationsMobile.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  loadList: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  // list
  firstRender: makeSelectFirstRender(),
  itemsList: makeSelectItemsList(),
  loading: selectCommunicationsLoading(),
  error: selectCommunicationsError(),
  params: makeSelectItemsListFilteringParams(),
  noMore: makeSelectItemsListNoMore(),
  totalAmount: makeSelectItemsListTotalAmount(),
  currentPage: makeSelectItemsListCurrentPage(),
});

function mapDispatchToProps(dispatch) {
  return {
    flushState: () => dispatch(flushStateCommunications()),
    loadList: (params, childId, firstRenderValue) =>
      dispatch(loadCommunications(params, childId, firstRenderValue)),
    dispatch,
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(CommunicationsMobile);
